import { getStoreRef } from '../store/StoreUtility';
import { isBlank, isIOS, isPresent } from './BooleanUtility';
import { AnalyticsManager, EventParameterKey, EventType } from '../analytics';
import { generateRequestHeader } from './UriUtility';
import Utility from './Utility';
import { messaging } from '../libraries/Firebase';
import { setCometChatUserData, updateUnreadChatCount } from '../actions/ActionTypes';
import { API_DOMAIN, COMET_CHAT_MESSAGE_SLUG, cometChatCredentials } from '../config/Constants';
import Config from 'react-native-config';
import { CometChatUIKit } from '../libraries/ReactNativeCometChatUiKit';
import CometChat from '../libraries/ReactNativeCometChatSdk';

var JSONAPIDeserializer = require('jsonapi-serializer').Deserializer;

export const resolveRestrictedShortLink = async (url) => {
  const parts = url.split('/');
  const link_id = parts[parts.length - 1] || null;
  try {
    const state = getStoreRef().getState();
    const response = await fetch(url, {
      method: 'GET',
      headers: generateRequestHeader(state),
    });
    const data = await response.json();
    if (!response.ok) {
      AnalyticsManager.logEvent(EventType.deeplink.DEEP_LINK_PREOPEN_ERROR,{
        [EventParameterKey.LINK_ID]: link_id,
        [EventParameterKey.RESPONSE_STATUS]: response.status,
        [EventParameterKey.ERROR_MESSAGE]: data?.error
      })
      return { changedSlug: '', type: '' };
    }
    const {
      type = '',
      slug = '',
      utm_medium = '',
      utm_campaign = '',
      utm_source = '',
      utm_content = '',
      utm_term = '',
    } = data;
    return {
      changedSlug: isPresent(type) && isPresent(slug) ? `/${type}/${slug}` : '',
      type,
      utm_medium,
      utm_campaign,
      utm_source,
      utm_content,
      utm_term,
    };
  } catch (error) {
    AnalyticsManager.logEvent(EventType.deeplink.DEEP_LINK_PREOPEN_ERROR, {
      [EventParameterKey.LINK_ID]: link_id,
      [EventParameterKey.ERROR_MESSAGE]: error.message,
    });
    return { changedSlug: '', type: '' };
  }
};
/**
 * Fetches data from an API with retry logic.
 * 
 * @param {string} url - The API endpoint.
 * @param {object} [options={}] - Fetch options such as headers or method.
 * @param {number} [maxRetries=10] - Maximum number of retry attempts.
 * @param {number} [delay=1000] - Delay between retries in milliseconds.
 * @returns {Promise<any>} - The fetched data.
 * @throws {Error} - Throws an error if all retries fail.
 */

export const fetchDataWithRetries = (url, maxRetries = 10, delay = 500, options = {}) => {
  let attemptCount = 0;

  // Function to create a delay
  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Recursive function to fetch data with retries
  function fetchWithRetry() {
    const defaultOptions = {
      method: 'GET',
      headers: generateRequestHeader(),
      ...options,
    };

    return fetch(url, defaultOptions)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .catch((error) => {
        attemptCount += 1;
        console.warn(`Attempt ${attemptCount} failed: ${error.message}. Retrying in ${delay}ms...`);

        if (attemptCount >= maxRetries) {
          throw new Error(`Failed to fetch data after ${maxRetries} attempts: ${error.message}`);
        }

        // Wait and retry
        return wait(delay).then(fetchWithRetry);
      });
  }

  // Start the retry process
  return fetchWithRetry();
};


 export const fetchCometChatUser = async (stopFurtherNavigation = false, callback = () => {}) => {
  const url = `${API_DOMAIN}/api/v1/comet_chat_users`;
  const cometChatData = getStoreRef().getState()?.UserAccountInfo?.cometChatUserInfo?.data;
  const cometChatAuthToken = cometChatData?.attributes?.authToken;
  if (cometChatData?.attributes?.uid) {
    CometChat.addMessageListener(
      cometChatData.attributes.uid,
      new CometChat.MessageListener({
        onTextMessageReceived: (textMessage) => {
          console.tron.log('Text message received successfully', textMessage);
        },
        onMediaMessageReceived: (mediaMessage) => {
          const unreadChatCount = getStoreRef().getState().UserAccountInfo.unreadChatCount;
          getStoreRef().dispatch(updateUnreadChatCount(unreadChatCount + 1));
          console.log('Media message received successfully', mediaMessage);
        },
        onCustomMessageReceived: (customMessage) => {
          const unreadChatCount = getStoreRef().getState().UserAccountInfo.unreadChatCount;
          getStoreRef().dispatch(updateUnreadChatCount(unreadChatCount + 1));
          console.tron.log('Custom message re1ceived successfully', unreadChatCount);
        },
      }),
    );
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: generateRequestHeader(),
    });
    if (response.ok) {
      // response.ok is shorthand for status >= 200 && status < 300
      const json = await response.json();

      if (!Utility.isBlank(json)) {
        const { data: { attributes: { uid = '' } = {} } = {} } = json;
        getStoreRef().dispatch(setCometChatUserData(json));
        if (uid) {
          CometChat.addMessageListener(
            uid,
            new CometChat.MessageListener({
              onTextMessageReceived: (textMessage) => {
                console.tron.log('Text message received successfully', textMessage);
              },
              onMediaMessageReceived: (mediaMessage) => {
                console.log('Media message received successfully', mediaMessage);
              },
              onCustomMessageReceived: (customMessage) => {
                const unreadChatCount = getStoreRef().getState()?.UserAccountInfo?.unreadChatCount;
                getStoreRef().dispatch(updateUnreadChatCount(unreadChatCount + 1));
                console.tron.log('Custom message re1ceived successfully', unreadChatCount);
              },
            }),
          );
        }
        CometChatUIKit.login({ uid })
          .then((user) => {
            callback(true);
            registerPushTokenAPI(user.authToken);
          })
          .catch((error) => {
            console.tron.log('Login failed with exception:', error);
          });
      } else {
        console.tron.log('Response JSON is blank');
      }
    } else {
      console.tron.log(`Error: HTTP ${response.status}`, response);
      // Optionally: Show an error message to the user
    }
  } catch (error) {
    console.tron.log(error, 'Fetch Error');
    // Optionally: Display error feedback to the user or retry logic
  }
};

const registerPushTokenAPI = async (authToken) => {
  const { cometChatAppID, cometChatAuthKey } = cometChatCredentials;
  try {
    const apnsToken = (await messaging().getAPNSToken()) || '';
    const fcmToken = (await messaging().getToken()) || '';
    const body = {
      authToken,
      timezone: 'Asia/Kolkata',
    };
    if (isIOS()) {
      body.platform = 'apns_react_native_device';
      body.deviceToken = apnsToken;
      body.providerId = Config.IOS_PROVIDER_ID;
    } else {
      body.platform = 'fcm_react_native_android';
      body.fcmToken = fcmToken;
      body.providerId = Config.ANDROID_PROVIDER_ID;
    }
    const response = await fetch(`https://${cometChatAppID}.api-in.cometchat.io/v3/notifications/push/v1/tokens`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        apiKey: cometChatAuthKey,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    console.tron.log(`${e}`, '------');
  }
};

export const fetchMessageConversation = async (id, callBack) => {
  try {
    const cometChatData = getStoreRef().getState()?.UserAccountInfo?.cometChatUserInfo?.data;
    const cometChatAuthToken = cometChatData?.attributes?.authToken;
    // Fetch data with retries
    const response = await fetchDataWithRetries(`${COMET_CHAT_MESSAGE_SLUG}${id}`, 10, 500);

    // Deserialize the response
    const deserializedData = await new JSONAPIDeserializer({
      typeAsAttribute: false,
      pluralizeType: true,
      keyForAttribute: 'camelCase',
    }).deserialize(response);
    // Handle missing auth token and fetchCometChatUser logic
    if (isBlank(cometChatAuthToken) && typeof fetchCometChatUser === 'function') {
      const success = await new Promise((resolve) => {
        fetchCometChatUser(isBlank(cometChatAuthToken), resolve);
      });
      if (success) {
        callBack(deserializedData);
      }
    } else {
      callBack(deserializedData);
    }
  } catch (error) {
    // Handle errors for fetching or deserialization
    console.error('Error processing message conversation:', error.message);
  }
};

export const fetchBotConversation = async (botType, callBack) => {
  try {
    const slug = `${API_DOMAIN}/api/v1/conversations/?bot_type=${'skincare_coach'}`;
    // Fetch data with retries using POST method
    const response = await fetchDataWithRetries(slug, 10, 500, {
      method: 'POST',
    });

    // Deserialize the response
    const deserializedData = await new JSONAPIDeserializer({
      typeAsAttribute: false,
      pluralizeType: true,
      keyForAttribute: 'camelCase',
    }).deserialize(response);
    const success = await new Promise((resolve) => {
      fetchCometChatUser(true, resolve);
    });
    if (success) {
      callBack(deserializedData);
    } else {
      callBack(deserializedData);
    }
  } catch (error) {
    // Handle errors for fetching or deserialization
    console.error('Error processing message conversation:', error.message);
  }
};


export const fetchApi = async (url, options = {}) => {
  const headers = generateRequestHeader();
  
  try {
    const response = await fetch(url, {
      method: options.method || 'GET',
      headers: {
        ...headers,
        ...options.headers,
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    if (response.status >= 200 && response.status < 300) {
      const json = await response.json();
      if (!isBlank(json)) {
        return json;
      }
    }
    return null;
  } catch (error) {
    console.error('API Error:', error);
    return null;
  }
};
